import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { PsychologyReverse } from "assets/material-custom-icons";
import { ToggleButtons } from "components/Report-generator/components/ToggleButtons";
import { useInvestorsSelector, useTaskDealsSelect, useTaskManageActions } from "store";
import { DealBase, IAiFile, IFile, IFileUploadType } from "store/types";

import { useFilePickerSelection } from "../hooks/use-file-picker-selection";
import { PickerModeConfig } from "../hooks/use-picker-filters";
import { border, s } from "../styles";
import { ListView } from "./ListView";
import { PickerFilterBar } from "./PickerFilterBar";
import { PickerHeader } from "./PickerHeader";
import { SelectionPanel } from "./SelectionPanel";
import { TreeView } from "./TreeView";

const FILE_PICKER_CONFIG: PickerModeConfig = {
  title: "Select files",
  subtitle: "",
  showTemplateName: false,
  foldersSelectable: false,
  showViewToggle: true,
  showUploadButton: true,
  showMagicButton: true,
  confirmLabel: (n) => (n === 0 ? "Select files" : "Select")
};

/* ─── File picker (file selection mode) props ─── */
export interface FilePickerProps {
  mode: "file";
  open: boolean;
  title?: string;
  subtitle?: string;
  currentFiles: IAiFile[];
  onClose: () => void;
  onSubmit: (data: { filesIds?: number[]; attachments: File[]; files?: IFile[] }) => Promise<void>;
  singleFileSelection?: boolean;
  dealId: number | null;
  getFilesOutsideDeal?: boolean;
  fileUploadType?: IFileUploadType[];
  attachedFiles?: File[];
  selectWithoutFiles?: boolean;
  acceptedFilesExtensions?: string[];
  onSelectFileForMe?: () => void;
  isFileForMeSelected?: boolean;
}

export const FilePicker: React.FC<FilePickerProps> = ({
  open,
  title,
  subtitle,
  currentFiles,
  onClose,
  onSubmit,
  singleFileSelection,
  dealId,
  getFilesOutsideDeal,
  fileUploadType,
  attachedFiles,
  selectWithoutFiles = false,
  acceptedFilesExtensions,
  onSelectFileForMe,
  isFileForMeSelected: _isFileForMeSelected
}) => {
  const config = FILE_PICKER_CONFIG;
  const investors = useInvestorsSelector();
  const investorMap = useMemo(() => {
    const map = new Map<number, string>();
    investors.forEach((inv) => map.set(inv.id, inv.name));
    return map;
  }, [investors]);

  /* In global forms (Ask AI) and global document file sections there is no deal in context,
     so the user must first pick a deal before its folders/files can be browsed. */
  const hasOwnDeal = typeof dealId === "number" && !Number.isNaN(dealId) && dealId > 0;
  const needsDealSelection = getFilesOutsideDeal && !hasOwnDeal;

  const deals = useTaskDealsSelect();
  const { getDealsAction } = useTaskManageActions();
  const [selectedDeal, setSelectedDeal] = useState<DealBase | null>(null);

  const effectiveDealId = hasOwnDeal ? dealId : (selectedDeal?.id ?? null);

  useEffect(() => {
    if (open && needsDealSelection && !deals.length) {
      getDealsAction();
    }
  }, [open, needsDealSelection, deals.length, getDealsAction]);

  const selection = useFilePickerSelection({
    currentFiles,
    isOpen: open,
    singleFileSelection,
    dealId: effectiveDealId,
    getFilesOutsideDeal,
    fileUploadType,
    attachedFiles,
    acceptedFilesExtensions
  });

  // Drop any stale selection when the browsed deal changes (files differ per deal).
  useEffect(() => {
    selection.clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeal?.id]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const handleSubmit = useCallback(async () => {
    try {
      selection.setLoading(true);
      const data = selection.getSubmitData();
      await onSubmit(data);
      selection.resetState();
      setSelectedDeal(null);
      onClose();
    } finally {
      selection.setLoading(false);
    }
  }, [selection, onSubmit, onClose]);

  const handleClose = useCallback(() => {
    selection.resetState();
    setSelectedDeal(null);
    onClose();
  }, [selection, onClose]);

  const handleSelectFileForMe = useCallback(() => {
    selection.resetState();
    setSelectedDeal(null);
    onClose();
    onSelectFileForMe?.();
  }, [selection, onClose, onSelectFileForMe]);

  const isSubmitDisabled = useMemo(() => {
    if (selectWithoutFiles) return false;
    return selection.selectedCount === 0;
  }, [selection.selectedCount, selectWithoutFiles]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, handleClose]);

  useEffect(() => {
    if (open) modalRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const displayTitle = title || config.title;
  const displaySubtitle = subtitle || config.subtitle;
  const showDealSelectionPrompt = needsDealSelection && !selectedDeal;
  // Folders resolve first, then files are fetched in the folders' onSuccess. Keep a single loader up for the
  // whole sequence so files don't pop into already-rendered folders and shift the tree.
  const isContentLoading = selection.foldersLoading || selection.filesLoading;

  return (
    <Box sx={s.overlay} onClick={handleClose}>
      <Box
        sx={s.modal}
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <PickerHeader title={displayTitle} subtitle={displaySubtitle} onClose={handleClose} />

        {/* Deal selector — shown when there is no deal in context (global forms / documents) */}
        {needsDealSelection && (
          <Box sx={{ mx: "20px", pt: "12px", pb: "14px", borderBottom: border() }}>
            <Autocomplete
              size="small"
              options={deals}
              value={selectedDeal}
              getOptionLabel={(option) => option.title}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, option) => setSelectedDeal(option)}
              renderInput={(params) => (
                <TextField {...params} label="Select a deal to browse its files" placeholder="Search deals..." />
              )}
            />
          </Box>
        )}

        {/* Controls */}
        {!showDealSelectionPrompt && (
          <Stack direction="row" alignItems="center" gap="12px" sx={{ px: "20px", py: "12px" }}>
            {config.showViewToggle && (
              <ToggleButtons
                value={selection.viewMode}
                onChange={(value) => selection.setViewMode(value)}
                options={[
                  { value: "tree", label: "Folders" },
                  { value: "list", label: "All items" }
                ]}
              />
            )}
            <TextField
              fullWidth
              size="small"
              placeholder="Search filename, classification..."
              value={selection.searchQuery}
              onChange={(e) => selection.setSearchQuery(e.target.value)}
              sx={{ "& .MuiInputBase-root": { height: 32 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                  </InputAdornment>
                )
              }}
            />
            {config.showMagicButton && onSelectFileForMe && (
              <Button
                variant="outlined"
                onClick={handleSelectFileForMe}
                startIcon={<PsychologyReverse sx={{ fontSize: 20, fill: "#26A6AE" }} />}
                sx={{
                  borderRadius: "999px",
                  bgcolor: "#FFFFFF",
                  borderColor: "#26A6AE",
                  color: "#26A6AE",
                  fontSize: 13,
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  "&:hover": { borderColor: "#39cad3" }
                }}
              >
                Select files for me
              </Button>
            )}
          </Stack>
        )}

        {/* Filter chips */}
        {!showDealSelectionPrompt && (
          <PickerFilterBar
            filters={selection.filters}
            hasActiveFilters={selection.hasActiveFilters}
            classificationOptions={selection.classificationOptions}
            setClassifications={selection.setClassifications}
            setInvestors={selection.setInvestors}
            setEffectiveDate={selection.setEffectiveDate}
            clearFilter={selection.clearFilter}
            clearAllFilters={selection.clearAllFilters}
          />
        )}

        {/* Results summary */}
        {!showDealSelectionPrompt && (selection.hasActiveFilters || selection.searchQuery) && (
          <Box sx={{ px: "20px", py: "8px", borderBottom: border() }}>
            <Typography sx={{ fontSize: 15 }}>
              Filter result: <strong>{selection.filteredFileCount} files</strong>
            </Typography>
          </Box>
        )}

        {/* Content area */}
        <Box sx={s.contentArea}>
          {showDealSelectionPrompt ? (
            <Stack alignItems="center" justifyContent="center" sx={{ height: "100%", minHeight: 360, px: "20px" }}>
              <Typography sx={{ fontSize: 15, color: "text.secondary", textAlign: "center" }}>
                Select a deal above to browse and select its files.
              </Typography>
            </Stack>
          ) : isContentLoading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ height: "100%", minHeight: 360 }}>
              <CircularProgress />
            </Stack>
          ) : selection.viewMode === "tree" ? (
            <TreeView selection={selection} investorMap={investorMap} />
          ) : (
            <ListView selection={selection} investorMap={investorMap} />
          )}
        </Box>

        {/* Upload button */}
        {!showDealSelectionPrompt && config.showUploadButton && (
          <Box sx={{ px: "20px", py: "10px" }}>
            <Chip
              icon={<FileUploadOutlinedIcon sx={{ fontSize: 14 }} />}
              label="Upload New Files"
              onClick={() => fileInputRef.current?.click()}
              size="small"
              sx={{ bgcolor: "#f6f8fc" }}
            />
            <input
              ref={fileInputRef}
              type="file"
              style={s.hiddenInput}
              multiple={!singleFileSelection}
              accept={acceptedFilesExtensions?.join(",") || undefined}
              onChange={selection.addFile}
            />
          </Box>
        )}

        {/* Selection panel */}
        {!showDealSelectionPrompt && <SelectionPanel mode="file" selection={selection} />}

        {/* Footer */}
        <Stack direction="row" alignItems="center" justifyContent="flex-end" gap="10px" sx={{ px: "20px", py: "14px" }}>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="contained" disabled={isSubmitDisabled} onClick={handleSubmit}>
            {config.confirmLabel(selection.selectedCount)}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};
